
import React from 'react';
import { Card } from "@/components/ui/card";
import { Brain, ChevronRight, Book, Trophy, Target } from 'lucide-react';
import type { PerformanceData } from './types';

interface SmartRecommendationChartProps {
  performanceData: PerformanceData[];
}

const SmartRecommendationChart: React.FC<SmartRecommendationChartProps> = ({ performanceData }) => {
  const getRecommendations = (data: PerformanceData[]) => {
    if (!data.length) return null;

    const recentPerformance = data.slice(-5);
    const averageAccuracy = recentPerformance.reduce((acc, curr) => 
      acc + ((curr.correct_answers / curr.total_questions) * 100), 0) / recentPerformance.length;

    const subjectPerformance = recentPerformance.reduce((acc, curr) => {
      if (!acc[curr.subject]) {
        acc[curr.subject] = {
          total: 0,
          count: 0
        };
      }
      acc[curr.subject].total += (curr.correct_answers / curr.total_questions) * 100;
      acc[curr.subject].count += 1;
      return acc;
    }, {} as Record<string, { total: number; count: number }>);

    const subjectRecommendations = Object.entries(subjectPerformance).map(([subject, perf]) => ({
      subject,
      accuracy: perf.total / perf.count,
      recommendation: perf.total / perf.count < 50 ? 'beginner' : 
                     perf.total / perf.count < 80 ? 'intermediate' : 'expert'
    }));

    return {
      overallAccuracy: averageAccuracy,
      subjectRecommendations
    };
  };

  const recommendations = getRecommendations(performanceData);

  if (!recommendations) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-2">Smart Recommendations</h3>
        <p className="text-gray-500">Complete more quizzes to get personalized recommendations.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Brain className="w-6 h-6 text-purple-500" />
        <h3 className="text-xl font-semibold">Smart Learning Recommendations</h3>
      </div>

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-24 text-sm font-medium">Overall Progress</div>
          <div className="flex-1 relative h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="absolute top-0 left-0 h-full bg-purple-500 transition-all duration-500"
              style={{ width: `${recommendations.overallAccuracy}%` }}
            />
          </div>
          <div className="w-16 text-sm font-medium">{Math.round(recommendations.overallAccuracy)}%</div>
        </div>

        <div className="grid gap-4">
          {recommendations.subjectRecommendations.map(rec => (
            <div key={rec.subject} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{rec.subject}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  rec.recommendation === 'expert' ? 'bg-green-100 text-green-800' :
                  rec.recommendation === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {rec.recommendation.charAt(0).toUpperCase() + rec.recommendation.slice(1)} Level
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                {rec.recommendation === 'beginner' ? (
                  <>
                    <Book className="w-4 h-4" />
                    <span>Focus on foundational concepts</span>
                  </>
                ) : rec.recommendation === 'intermediate' ? (
                  <>
                    <Target className="w-4 h-4" />
                    <span>Practice with moderate difficulty questions</span>
                  </>
                ) : (
                  <>
                    <Trophy className="w-4 h-4" />
                    <span>Challenge yourself with expert-level content</span>
                  </>
                )}
                <ChevronRight className="w-4 h-4 ml-auto" />
              </div>

              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`absolute top-0 left-0 h-full transition-all duration-500 ${
                      rec.recommendation === 'expert' ? 'bg-green-500' :
                      rec.recommendation === 'intermediate' ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${rec.accuracy}%` }}
                  />
                </div>
                <span className="text-xs font-medium w-12">{Math.round(rec.accuracy)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default SmartRecommendationChart;
